import { FilePdfOutlined } from '@ant-design/icons';
import { type AllegatoRichiestaDTO } from '@framework/api-client/api/inquiries-compile/schemas/allegatoRichiestaDTO.ts';
import { type DettaglioFirma } from '@framework/api-client/api/inquiries-compile/schemas/dettaglioFirma.ts';
import { SummaryDetailCard } from '@framework/ui/components/summary/SummaryDetailCard.tsx';
import { Alert, Flex } from 'antd';
import { useTranslation } from 'react-i18next';
import { DownloadSummaryButton } from '../download-pdf/DownloadSummaryButton.tsx';
import { SignerHeader } from '../signer/SignerHeader.tsx';
import { SignatureModalButton } from '../signer/signature/SignatureModalButton.tsx';
import { useSignedSummaryFile } from './useSignedSummaryFile.ts';

export interface ISummarySignerReadonlyViewProps {
  submissionId: string;
  /** Se `true`, scarica il riepilogo firmato; altrimenti quello non firmato. */
  hasSignedSummary: boolean;
  /**
   * Se `true`, il recupero degli allegati è fallito e non è possibile
   * stabilire se esiste un riepilogo firmato: il download viene disabilitato
   * per evitare di scaricare il riepilogo generato al posto del firmato.
   */
  attachmentsError?: boolean;
  /**
   * Se `true`, il recupero degli allegati è ancora in corso: come per
   * `attachmentsError` lo stato firmato è indeterminato e il download viene
   * disabilitato, ma senza mostrare alcun avviso.
   */
  attachmentsLoading?: boolean;
  disabled?: boolean;
  signedSummaryAttachment?: AllegatoRichiestaDTO | null;
  signatures?: DettaglioFirma[];
}

/**
 * Solo download del riepilogo della richiesta (senza upload).
 * Usato in backoffice e in frontoffice quando l'upload del firmato non è richiesto.
 * Se sono presenti dati di firma sul documento firmato, mostra il pulsante
 * "Verifica Firma" per aprire la modal di riepilogo dei dati di firma.
 */
export function SummarySignerReadonlyView(
  props: Readonly<ISummarySignerReadonlyViewProps>,
) {
  const {
    submissionId,
    hasSignedSummary,
    attachmentsError,
    attachmentsLoading,
    disabled,
    signedSummaryAttachment,
    signatures,
  } = props;
  const { t } = useTranslation();

  const signedFile = useSignedSummaryFile(
    hasSignedSummary,
    signedSummaryAttachment,
  );

  return (
    <div>
      <SummaryDetailCard variant="signer">
        <SignerHeader
          icon={<FilePdfOutlined aria-hidden="true" />}
          title={t(
            'riepilogo-firmato.download-riepilogo-richiesta',
            'Download del Riepilogo della Richiesta',
          )}
          description={t(
            'riepilogo-firmato.scarica-riepilogo-desc',
            'È possibile scaricare il documento di Riepilogo della Richiesta.',
          )}
        />
        {attachmentsError && (
          <Alert
            type="warning"
            showIcon
            message={t(
              'riepilogo-firmato.errore-verifica-firmato',
              'Non è stato possibile verificare la presenza del riepilogo firmato. Ricaricare la pagina per riprovare.',
            )}
            style={{ marginBottom: 16 }}
          />
        )}
        <Flex align="center" gap={8}>
          <DownloadSummaryButton
            submissionId={submissionId}
            signed={hasSignedSummary}
            disabled={disabled || attachmentsError || attachmentsLoading}
            ignoreMobile
            forceIcon
          />
          <SignatureModalButton
            signedFile={signedFile}
            signatures={signatures}
            disabled={disabled}
            type="default"
            size="large"
          >
            {t('signer.verifica-firma', 'Verifica Firma')}
          </SignatureModalButton>
        </Flex>
      </SummaryDetailCard>
    </div>
  );
}
